/**
 * SAML SSO Service
 * Handles SAML-related API calls and SSO status checks
 */

import axios from 'axios';

/**
 * Get SAML status for a tenant
 * @param {string} tenantCode - Tenant code
 * @returns {Promise<Object>} SAML status { samlEnabled, passwordAuthDisabled, allowPasswordFallback }
 */
export async function getSAMLStatus(tenantCode) {
    try {
        const response = await axios.get(`/auth/saml/status/${tenantCode}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching SAML status:', error);
        // Return default values if the endpoint fails
        return {
            samlEnabled: false,
            passwordAuthDisabled: false,
            allowPasswordFallback: true,
        };
    }
}

/**
 * Initiate SAML SSO login
 * Redirects the browser to the IdP
 * @param {string} tenantCode - Tenant code
 */
export function initiateSAMLLogin(tenantCode) {
    // Simply redirect to the SAML login endpoint
    // The server will handle generating the AuthnRequest and redirecting to the IdP
    window.location.href = `/auth/saml/login/${tenantCode}`;
}

/**
 * Get SAML SP metadata URL
 * @param {string} tenantCode - Tenant code
 * @returns {string} URL to SP metadata
 */
export function getSAMLMetadataUrl(tenantCode) {
    return `/auth/saml/metadata/${tenantCode}`;
}

/**
 * Parse SAML error code into user-friendly message
 * @param {string} errorCode - SAML error code from URL
 * @returns {string} User-friendly error message
 */
export function getSAMLErrorMessage(errorCode) {
    const errorMessages = {
        'SAML_NOT_ENABLED': 'SSO is not available for this organization.',
        'SAML_INVALID_RESPONSE': 'Authentication failed. Please try again.',
        'SAML_SIGNATURE_INVALID': 'Authentication failed. Please try again.',
        'SAML_ASSERTION_EXPIRED': 'Your session expired. Please try again.',
        'SAML_REPLAY_DETECTED': 'Authentication failed. Please try again.',
        'SAML_USER_NOT_FOUND': 'Your account was not found. Please contact your administrator.',
        'SAML_USER_INACTIVE': 'Your account is disabled. Please contact your administrator.',
        'SAML_TENANT_ACCESS': 'You do not have access to this application. Please contact your administrator.',
        'SAML_MFA_REQUIRED': 'Please complete multi-factor authentication setup.',
        'SAML_INTERNAL_ERROR': 'An error occurred. Please try again later.',
        'TENANT_INACTIVE': 'This organization is not currently active.',
    };

    return errorMessages[errorCode] || 'An error occurred during sign-in. Please try again.';
}

export default {
    getSAMLStatus,
    initiateSAMLLogin,
    getSAMLMetadataUrl,
    getSAMLErrorMessage,
};
